import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/core/composables/useFetch'
import { useFetch as useFetchV3 } from '@/modules/shared/composables/use-fetch'
import { useFetch as useFetchV4 } from '@/modules/core/composables/useFetch'
import { downloadAndSave } from '@/modules/filing/utils/utils'
import { addItems, clearItems } from '@/modules/shared/utils/store'
import { CID } from '@/modules/shared/utils/store.types'
import { Money } from '@/modules/shared/utils/money'
import { Invoice } from '../types'

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

type ID = number | string

type InvoiceOld = {
  _cid: CID
  _key: string
  id: string
  amount: Money
  entity: {
    _cid: CID
    id: ID
    entity_type: string
    name: string
  }
  date: string
  investor: {
    _cid: CID
    _custom_type: string
    id: ID
    name: string
    wiring_instructions: {
      disbursement_method: string
      disbursement_wire_bank_name: string
      disbursement_wire_bank_address: string
      disbursement_wire_bank_routing_number: string
      disbursement_wire_bank_account_number: string
      disbursement_wire_bank_swift_code: string
      disbursement_wire_account_name: string
      disbursement_wire_for_further_credit_to: string
      disbursement_check_receiver_name: string
      disbursement_other_details: string
    }
  }
  is_call_invoice: boolean
  management_fees: Money
  marked_as_paid: boolean
  name: string
  notes: string
  other_fees: Money
  status: string
  total_amount: Money
  wiring_instructions: string
  paid_at: string
}

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

type InvoiceMap = Map<CID, Invoice>

export const useInvoiceStore = defineStore('investing/invoiceStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/${route.params.slug}/investing`)

  const items = ref<InvoiceMap>(new Map())
  const invoices = computed(() => Array.from(items.value.keys()).map((key) => items.value.get(key)))

  const fetchInvoices = async (investable_type, investable_id) => {
    const { data, error } = await useFetchV3(`${baseUrl.value}/${investable_type}/${investable_id}/invoices`)
      .get()
      .json<{ data: Invoice[] }>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }

    clearItems(items)
    addItems(items, data.value.data)
  }

  const fetchInvoice = async (invoice_id, { slug }: { slug: string }) => {
    return await useFetch(`/v4/${slug}/investing/invoices/${invoice_id}`).get<Invoice>()
  }

  const markMultipleInvoicesAsPaid = async (payload) => {
    try {
      await useFetchV4(`/v4${baseUrl.value}/invoices/mark-multiple-as-paid`).post(payload, { silent: false })
    } catch (error) {
      console.error(error)
    }
  }

  const markMultipleInvoicesAsUnpaid = async (invoice_ids) => {
    try {
      await useFetchV4(`/v4${baseUrl.value}/invoices/mark-multiple-as-unpaid`).post({ invoice_ids }, { silent: false })
    } catch (error) {
      console.error(error)
    }
  }

  const sendReminder = async (invoice_id) => {
    const { data, error } = await useFetchV3(`${baseUrl.value}/invoice/${invoice_id}/send_reminder`).post().json<{}>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }
  }

  const markInvoiceAsUnpaid = async (invoice_id) => {
    const invoice_ids = [invoice_id]
    await markMultipleInvoicesAsUnpaid(invoice_ids)
  }

  const removeInvoice = async (invoice_id) => {
    const { data, error } = await useFetchV3(`${baseUrl.value}/invoice/${invoice_id}/remove`).delete().json<{}>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }
  }

  const downloadInvoicePdf = async (invoice_id) => {
    try {
      const response = await useFetchV4(`/v4${baseUrl.value}/invoices/${invoice_id}/pdf`).download({ silent: false })
      await downloadAndSave(response, `invoice-${invoice_id}.pdf`)
    } catch (error) {
      console.error('Error downloading invoice PDF:', error)
      throw error
    }
  }

  return {
    items,
    invoices,

    fetchInvoices,
    fetchInvoice,
    markInvoiceAsUnpaid,
    markMultipleInvoicesAsPaid,
    markMultipleInvoicesAsUnpaid,
    removeInvoice,
    sendReminder,
    downloadInvoicePdf,
  }
})
